/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import type { TFunction } from 'i18next';
import { FaPlus, FaTrashAlt } from 'react-icons/fa';
import U2000ActionBtn from '../../../../u2000Core/components/buttons/U2000ActionBtn';
import U2000DetailTable from '../../../../u2000Core/components/tables/detail/U2000DetailTable';
import { U2000IdColumnDef, type U2000TableColumn } from '../../../../u2000Core/components/tables/basic/U2000BasicTableList';
import U2000ResponsiveToolbar from '../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar';
import { useU2000AuthContext } from '../../../../u2000Core/context/auth/U2000UseAuthContext';
import { U2000FormStateEnumState } from '../../../../u2000Core/reducer/formReducer/U2000FormState';
import type { U2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useU2000GridStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { NS, U3206ROLES } from '../../../U3206';
import { useU3206Context } from '../../../U3206Context';
import type { U3206AiUserGroupFormRow, U3206GroupMemberLink } from '../../../U3206Service';
import U3206UsersAddDlg from './U3206UsersAddDlg';

type U3206UserGridRow = U3206GroupMemberLink & { id: string };

interface U3206UsersTabProps {
  formStateHandlers: U2000FormStateHandlers<U3206AiUserGroupFormRow>;
  t: TFunction;
}

function toGridRow(link: U3206GroupMemberLink): U3206UserGridRow {
  return {
    ...link,
    id: link.id ?? link.userId,
  };
}

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    { ...U2000IdColumnDef },
    {
      name: 'username',
      label: t('lblUsername', { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: 'w-[30%]',
      type: 'string',
      align: 'left',
    },
    {
      name: 'firstName',
      label: t('lblFirstName', { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: 'w-[35%]',
      type: 'string',
      align: 'left',
    },
    {
      name: 'lastName',
      label: t('lblLastName', { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: 'w-[35%]',
      type: 'string',
      align: 'left',
    },
  ];
}

function U3206UsersTab({ formStateHandlers, t }: U3206UsersTabProps) {
  const { initLU } = useU3206Context();
  const { hasRole } = useU2000AuthContext();
  const data = formStateHandlers.getData();
  const isEditMode = formStateHandlers.getFormState() === U2000FormStateEnumState.EDIT;
  const members = data?.members ?? [];
  const [addOpen, setAddOpen] = useState(false);
  const gridStateHandlers = useU2000GridStateHandlers<U3206UserGridRow>();

  const membersDataKey = useMemo(() => {
    const ids = members.map((m) => m.userId).filter(Boolean).sort();
    return `${data?.id ?? ''}_${members.length}_${ids.join(',')}`;
  }, [data?.id, members]);

  const setMembers = (next: U3206GroupMemberLink[]) => {
    formStateHandlers.onChange?.(
      { target: { id: 'members', value: next } } as unknown as ChangeEvent<HTMLInputElement>,
      next,
    );
  };

  useEffect(() => {
    const columns = getColumnsDef(t);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(
      columns
        .filter((col) => col.isDefaultSortedColumn)
        .map((col) => ({ name: col.name, sortOrder: 'ASC' as const })),
    );
    const list = members.map(toGridRow);
    gridStateHandlers.setData(list);
    gridStateHandlers.setTotalRows(list.length);
    gridStateHandlers.setErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridStateHandlers volontairement exclu
  }, [membersDataKey, t]);

  const selectedRows = gridStateHandlers.getSelectedRows();
  const canEdit =
    isEditMode && (hasRole(U3206ROLES.EDIT) || hasRole(U3206ROLES.ADD));
  const deleteDisabled = !canEdit || selectedRows.length === 0;

  const handleDelete = () => {
    if (deleteDisabled) return;
    const selectedIds = new Set(selectedRows.map((row) => row.userId));
    setMembers(members.filter((m) => !selectedIds.has(m.userId)));
  };

  const handleConfirmAdd = (links: U3206GroupMemberLink[]) => {
    const existing = new Set(members.map((m) => m.userId));
    const toAdd = links.filter((link) => !existing.has(link.userId));
    if (toAdd.length === 0) return;
    setMembers([...members, ...toAdd]);
  };

  const actions = useMemo(
    () => [
      {
        key: 'add',
        label: t('btnAdd', { ns: NS }),
        handleClick: () => setAddOpen(true),
        hasRole: hasRole(U3206ROLES.EDIT) || hasRole(U3206ROLES.ADD),
        disabled: !canEdit || initLU.users.length === 0,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: 'delete',
        label: t('btnDelete', { ns: NS }),
        handleClick: handleDelete,
        hasRole: hasRole(U3206ROLES.EDIT) || hasRole(U3206ROLES.ADD),
        disabled: deleteDisabled,
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleDelete stable via members snapshot
    [t, canEdit, deleteDisabled, hasRole, initLU.users.length, selectedRows.length, membersDataKey],
  );

  if (initLU.users.length === 0 && members.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('lblNoUsers', { ns: NS })}</p>;
  }

  return (
    <>
      <U3206UsersAddDlg
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onConfirm={handleConfirmAdd}
        catalog={initLU.users}
        linkedUserIds={members.map((m) => m.userId)}
        currentGroupId={data?.id}
        t={t}
      />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={canEdit}
        label={t('lblUserList', { count: gridStateHandlers.getTotalRows(), ns: NS })}
      />
    </>
  );
}

export default U3206UsersTab;
